import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { FormMode } from '@/enums'
import { LoaderFunction, redirect } from 'react-router-dom'
import type { est_licenza, azienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { defer, makeAction } from 'react-router-typesafe'
import { HttpResponseModel, ODataClientError } from '@odata2ts/http-client-api'
import { ODataModelResponseV4 } from '@odata2ts/odata-core'
import { SoftwareAltrePiattaformeEditor } from '@/routes/software/altre-piattaforme/SoftwareAltrePiattaformeEditor'
import { Roles } from '@/auth/azure/Roles'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { createDescriptor } from '@/components/BreadCrumbs/BreadCrumbs'

export const softwareAltrePiattaformeEditorRouteLoader = (async ({
  params,
}: RouteFunctionParams<'clientId' | 'swAltrePiattaformeId'>) => {
  const qsAdminApi = useQsAdminApiManager.getState().service
  const clientId = params.clientId

  let azienda: HttpResponseModel<ODataModelResponseV4<azienda>> | undefined
  if (clientId)
    azienda = await qsAdminApi.azienda(Number(clientId)).query((builder, azienda) => {
      builder.select('id', 'nome')
    })
  let title = azienda ? `${azienda.data.nome} - ` : ``

  let swLicense: est_licenza
  if (params.swAltrePiattaformeId) {
    const currentSwId = +(params.swAltrePiattaformeId as string)
    if (Number.isNaN(currentSwId)) throw new Error('Invalid swAltrePiattaformeId')
    const getSwLicense = await qsAdminApi
      .est_licenza(currentSwId)
      .query((builder, qqs_licenza) => {
        builder.expanding('sede', (sedeBuilder) => {
          sedeBuilder.expanding('azienda', (companyBuilder) => {
            companyBuilder.expand('sedi')
          })
        })
        builder.expand('software')
        builder.expand('piano_mantenimento')
        builder.expand('stato_noleggio')
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Licenza non trovata')
        throw new Error()
      })
    swLicense = getSwLicense.data satisfies est_licenza
    title += swLicense.seriale ?? 'Seriale non definito'
    if (clientId) {
      const swEstResp = await getSwLicense
      const swEst = swEstResp.data satisfies est_licenza
      if (swEst?.sede?.azienda && swEst?.sede?.azienda.id !== Number(clientId)) {
        throw new Error(
          `Il software ${swEst.n_contratto ?? swEst.seriale ?? swEst.id} appartiene al cliente ${swEst?.sede?.azienda.nome} e non al cliente corrente`,
        )
      }
    }
  } else {
    title += 'Nuova licenza'
    swLicense = {
      id: 0,
      seriale: null,
      altro_fornitore: false,
      versione: null,
      data_vendita: null,
      scadenza: null,
      rete: false,
      n_contratto: null,
      postazioni: 0,
      durata: 0,
      note: null,
    }
  }

  const getClients = qsAdminApi.azienda().query((builder, azienda) => {
    builder.orderBy(azienda.nome.asc())
    builder.select('id', 'nome')
    builder.filter(azienda.sedi.any())
    builder.filter(clientId ? azienda.id.eq(Number(clientId)) : null)
  })

  const getAltrePiattaformePlatforms = qsAdminApi.est_software().query((builder, software) => {
    builder.orderBy(software.nome.asc())
  })

  const getAltrePiattaformeMaintenancePlans = qsAdminApi.est_piano_mantenimento().query((builder, piano) => {
    builder.orderBy(piano.nome.asc())
  })

  const getAltrePiattaformeRentStatus = qsAdminApi.est_stato_noleggio().query((builder, stato) => {
    builder.orderBy(stato.nome.asc())
  })

  return defer({
    title,
    swLicense,
    getClients,
    getAltrePiattaformePlatforms,
    getAltrePiattaformeMaintenancePlans,
    getAltrePiattaformeRentStatus,
    getUserInfo: getAzureUserInformation(),
    defaultCRUDAllowedRoles: [Roles.GlobalAdministrator, Roles.Administrator, Roles.Supervisor],
  })
}) satisfies LoaderFunction

export const SoftwareAltrePiattaformeEditorRoute = {
  path: ':swAltrePiattaformeId',
  element: <SoftwareAltrePiattaformeEditor mode={FormMode.Edit} />,
  loader: softwareAltrePiattaformeEditorRouteLoader,
  action: makeAction(async ({ request, params }) => {
    const swAltrePiattaformeData = (await request.json()) as est_licenza
    const res = await useQsAdminApiManager
      .getState()
      .service.est_licenza(swAltrePiattaformeData.id)
      .patch(swAltrePiattaformeData)
    return null
  }),
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: (match) => ({
      label: match.data.swLicense.seriale ?? 'Seriale non definito',
    }),
  },
} as RoleRouteObject

export const SoftwareAltrePiattaformeCreatorRoute = {
  path: 'new',
  element: <SoftwareAltrePiattaformeEditor mode={FormMode.Create} />,
  loader: softwareAltrePiattaformeEditorRouteLoader,
  index: true,
  action: makeAction(async ({ request, params }: RouteFunctionParams) => {
    const swAltrePiattaformeData = (await request.json()) as est_licenza
    const res = await useQsAdminApiManager.getState().service.est_licenza().create(swAltrePiattaformeData)
    return redirect(`../${res.data.id}`)
  }),
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: () => createDescriptor,
  },
} as RoleRouteObject
